import type { EnemyType } from '../balance';
import { STARFALL_ABILITIES } from '../arenaAbilities';
import { STARFALL_PASSIVE_KEYS } from '../passives';
import type { LevelDefinition } from './types';

const ENEMIES: Readonly<Record<string, EnemyType>> = {
  coalMite: {
    key: 'coalMite',
    hp: 20,
    contactDmg: 5,
    speedPerSec: 50,
    radius: 10,
    xp: 1,
    color: 0xe55b4f,
    kind: 'chase',
    sprite: 'coalMite',
  },
  prismDrifter: {
    key: 'prismDrifter',
    hp: 12,
    contactDmg: 4,
    speedPerSec: 96,
    radius: 7,
    xp: 1,
    color: 0x7fe7ff,
    kind: 'weave',
    sprite: 'prismDrifter',
  },
  railSentinel: {
    key: 'railSentinel',
    hp: 86,
    contactDmg: 9,
    speedPerSec: 0,
    radius: 12,
    xp: 3,
    color: 0x8bb5ff,
    kind: 'stationary',
    sprite: 'railSentinel',
    railEmitter: { cooldownSec: 5.8, railCount: 1 },
  },
  kilnHound: {
    key: 'kilnHound',
    hp: 225,
    contactDmg: 16,
    speedPerSec: 62,
    radius: 15,
    xp: 7,
    color: 0xffc266,
    kind: 'charge',
    sprite: 'kilnHound',
  },
  starScribe: {
    key: 'starScribe',
    hp: 88,
    contactDmg: 10,
    speedPerSec: 40,
    radius: 10,
    xp: 5,
    color: 0xec78e1,
    kind: 'ranged',
    sprite: 'starScribe',
    rangedAttack: {
      rangePx: 520,
      sprite: 'starShot',
      color: 0xec78e1,
      damage: 11,
      speed: 160,
      radius: 6,
      lifeSec: 4.6,
      bounces: 2,
    },
  },
  magnetBeetle: {
    key: 'magnetBeetle',
    hp: 300,
    contactDmg: 18,
    speedPerSec: 54,
    radius: 14,
    xp: 14,
    color: 0x7aa8e8,
    kind: 'chase',
    sprite: 'magnetBeetle',
    magnetEmitter: { radiusPx: 165, pullPerSec: 110 },
  },
  conduitShard: {
    key: 'conduitShard',
    hp: 38,
    contactDmg: 10,
    speedPerSec: 120,
    radius: 8,
    xp: 2,
    color: 0x74edff,
    kind: 'chase',
    sprite: 'conduitShard',
  },
  conduitGolem: {
    key: 'conduitGolem',
    hp: 540,
    contactDmg: 25,
    speedPerSec: 42,
    radius: 20,
    xp: 25,
    color: 0x7396db,
    kind: 'split',
    sprite: 'conduitGolem',
    split: { enemyKey: 'conduitShard', count: 2 },
  },
};

const BOSSES: Readonly<Record<string, EnemyType>> = {
  railwright: {
    key: 'railwright',
    hp: 2900,
    contactDmg: 28,
    speedPerSec: 34,
    radius: 36,
    xp: 150,
    color: 0xb2cdff,
    kind: 'summon',
    sprite: 'railwright',
    summon: { enemyKey: 'railSentinel', count: 2, cooldownSec: 4 },
    railEmitter: { cooldownSec: 7.2, railCount: 2 },
  },
  meteorHart: {
    key: 'meteorHart',
    hp: 5400,
    contactDmg: 40,
    speedPerSec: 106,
    radius: 31,
    xp: 240,
    color: 0xffbc64,
    kind: 'charge',
    sprite: 'meteorHart',
    railEmitter: { cooldownSec: 6.2, railCount: 1, rerouteActive: true },
  },
  starfallEngine: {
    key: 'starfallEngine',
    hp: 11600,
    contactDmg: 64,
    speedPerSec: 84,
    radius: 33,
    xp: 350,
    color: 0xf260ce,
    kind: 'ranged',
    sprite: 'starfallEngine',
    rangedAttack: {
      rangePx: 580,
      sprite: 'starShot',
      color: 0xf260ce,
      damage: 16,
      speed: 190,
      radius: 7,
      lifeSec: 4.8,
      bounces: 3,
    },
    railEmitter: { cooldownSec: 5.6, railCount: 2 },
  },
};

/** Approved second Expansion Pack arena. It remains a 15-minute survivor run but turns
 * spatial pressure into readable directional momentum rather than Verdant Still walls. */
export const STARFALL_CONDUIT_LEVEL: LevelDefinition = {
  id: 'expansion_2',
  displayName: 'The Starfall Conduit',
  entitlement: 'expansion',
  runLengthSec: 900,
  musicTrack: 'starfallConduit',
  victoryBossKey: 'starfallEngine',
  schedule: [
    { t: 120, kind: 'event', banner: 'STARFALL SURGE', burst: 31 },
    { t: 150, kind: 'rail', banner: 'GRAVITY RAILS', gravityRailCount: 1 },
    { t: 300, kind: 'boss', bossKey: 'railwright', banner: 'THE RAILWRIGHT' },
    { t: 480, kind: 'rail', banner: 'CONDUIT REVERSAL', gravityRailCount: 2 },
    { t: 600, kind: 'boss', bossKey: 'meteorHart', banner: 'THE METEOR HART' },
    { t: 720, kind: 'rail', banner: 'SKYFORGE OVERLOAD', gravityRailCount: 2 },
    { t: 900, kind: 'boss', bossKey: 'starfallEngine', banner: 'THE STARFALL ENGINE' },
  ],
  tierUnlocks: [
    { t: 0, key: 'coalMite', w: 10 },
    { t: 0, key: 'prismDrifter', w: 6 },
    { t: 90, key: 'railSentinel', w: 4 },
    { t: 210, key: 'kilnHound', w: 4 },
    { t: 330, key: 'starScribe', w: 3 },
    { t: 360, key: 'magnetBeetle', w: 2 },
    { t: 450, key: 'conduitGolem', w: 1 },
  ],
  enemyTypes: ENEMIES,
  bossTypes: BOSSES,
  weaponKeys: [
    'prismScepter', 'solarFan', 'cometLance', 'arcRelay', 'gravityWell', 'moonletArray',
    'coronaMist', 'auroraArc', 'redGiant', 'cometCrown', 'orbitalStorm', 'prismCataclysm',
  ],
  startingWeaponKey: 'prismScepter',
  passiveKeys: STARFALL_PASSIVE_KEYS,
  abilities: STARFALL_ABILITIES,
  spawnTuning: {
    distancePx: 720,
    distanceJitterPx: 140,
    maxActive: 610,
    maxPerTick: 10,
    baseSpawnPerSec: 2.7,
    maxSpawnPerSec: 32,
    rampToMaxSec: 480,
    hpScalePerMin: 0.1,
    dmgScalePerMin: 0.06,
  },
  difficultyOverrides: {
    easy: {
      spawnMul: 0.32,
      xpMul: 2.5,
      dmgTakenMul: 0.22,
      enemyHpMul: 0.72,
      hazardForceMul: 0.65,
      healthOrbDropChance: 0.08,
      healthOrbHealFrac: 0.1,
    },
  },
  visualTheme: {
    backgroundColor: 0x111517,
    gridColor: 0x30363a,
    groundTexture: 'conduit',
    obstacleTexture: 'steelPylon',
    puddleTexturePrefix: 'coolant',
    foliage: 'forge',
    altarTexture: 'conduitAltar',
  },
  gravityRails: {
    maxActiveRails: 2,
    lengthPx: 340,
    widthPx: 34,
    telegraphSec: 0.8,
    durationSec: 4.6,
    forcePerSec: 210,
    minDistancePx: 150,
    maxDistancePx: 280,
  },
};
